import { useState, useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Send,
  Images,
  MessageCirclePlus,
  Loader2,
  ArrowDown,
  X,
} from "lucide-react";
import { useFetchConversationChunks } from "../hooks/useFetchConversationChunks";
import { Chunk } from "./Chunk";
import { useSidebar } from "./ui/sidebar";
import { useUser } from "../contexts/UserContext";
import { MessageBubbleInput } from "./MessageBubbleInput";
import { extractTextFromImagesWithGemini } from "../data/gemini";
import { ImageProcessingDialog } from "./ImageProcessingDialog";
import { ConversationDropdownMenu } from "./ConversationDropdownMenu";
import { Input } from "@/components/ui/input";
import { useToast } from "../hooks/use-toast";
import { useUpsellDialog } from "../contexts/UpsellDialogContext";
import { AutoResizeTextarea } from "@/components/ui/auto-resize-textarea";
import { useAuth } from "../contexts/AuthContext";
import analytics from "../lib/analytics";
import { usePaymentUpdateDialog } from "../contexts/PaymentUpdateDialogContext";
import * as Sentry from "@sentry/react";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../data/firebase";
import { needsPaymentUpdate } from "../data/access";
import { hasRealTargetName } from "../data/helpers";

interface EnhancedChatInputScreenProps {
  conversationId: string;
  targetName: string;
  conversationName: string;
}

const SHOW_MANUAL_MESSAGE_BUTTONS_IN_NEW_CHAT = false;
const QUICK_TIP_DISMISSED_KEY = "options-ai-quick-tip-dismissed";

export function EnhancedChatInputScreen({
  conversationId,
  targetName,
  conversationName,
}: EnhancedChatInputScreenProps) {
  const [inputText, setInputText] = useState("");
  const [showQuickTip, setShowQuickTip] = useState(
    () =>
      typeof window === "undefined" ||
      window.localStorage.getItem(QUICK_TIP_DISMISSED_KEY) !== "true"
  );
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showNewMessage, setShowNewMessage] = useState<
    "user" | "target" | null
  >(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Image processing state
  const [imagesProcessing, setImagesProcessing] = useState(false);
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [extractedMessages, setExtractedMessages] = useState(0);

  const { userData } = useUser();
  const userName = userData?.name || "You";
  const targetLabel = hasRealTargetName(targetName) ? targetName : "Them";
  const { isMobile } = useSidebar();

  const { toast } = useToast();
  const { openUpsell } = useUpsellDialog();
  const { openPaymentUpdate } = usePaymentUpdateDialog();
  const { access } = useAuth();

  // Free requests apply only to users who have never held any entitlement.
  const canUseFreeRequest =
    !access.everEntitled && Boolean(userData?.freeRequestsRemaining);

  /**
   * Gate for paid actions. Returns true if the action may proceed; otherwise
   * opens the right dialog (card update for a failed Stripe payment, upsell
   * for everything else) and returns false.
   */
  const ensureAccess = (): boolean => {
    if (access.hasAccess || canUseFreeRequest) return true;
    if (needsPaymentUpdate(access)) {
      openPaymentUpdate();
    } else {
      openUpsell();
    }
    return false;
  };

  const {
    lastChunk,
    chunks,
    loading,
    loadingFirstTime,
    error,
    addNewMessage,
    editMessage,
    deleteMessage,
    analyzeChat,
    generateAIResponse,
    generateRecommendations,
    responseLoading,
    processExtractedMessages,
    deleteAIChat,
    moveMessage,
  } = useFetchConversationChunks(conversationId, targetName);

  const isNewChat =
    chunks.length === 0 ||
    (chunks.length === 1 &&
      !chunks[0].aiChat &&
      chunks[0].conversationMessages.length === 0);

  useEffect(() => {
    // Wait for next tick to ensure content is rendered
    // const timer = setTimeout(() => {
    if (!loading) {
      if (scrollAreaRef.current) {
        const scrollContainer = scrollAreaRef.current.querySelector(
          "[data-radix-scroll-area-viewport]"
        );
        if (scrollContainer) {
          // Disable smooth scrolling temporarily
          (scrollContainer as HTMLElement).style.scrollBehavior = "auto";
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
          // Re-enable smooth scrolling
          (scrollContainer as HTMLElement).style.scrollBehavior = "smooth";
        }
      }
    }
    // }, 0);

    // return () => clearTimeout(timer);
  }, [loading]); // Add chunks as dependency to ensure this runs after content loads

  const handleUploadScreenshot = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files?.length) return;

    try {
      // Reset processing state
      setImagesProcessing(true);
      setProcessingDialogOpen(true);
      setCurrentImageIndex(0);
      setTotalImages(files.length);
      setExtractedMessages(0);

      const startTime = performance.now();

      const base64Images = await Promise.all(
        Array.from(files).map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );

      analytics.track("Upload Screenshots", {
        numImages: base64Images.length,
      });

      // Update progress callback
      const updateProgress = (index: number, messagesFound: number) => {
        setCurrentImageIndex(index);
        setExtractedMessages(messagesFound);
      };

      // Use Gemini instead of Firebase function
      const data = await extractTextFromImagesWithGemini(
        base64Images,
        updateProgress
      );

      const endTime = performance.now();
      const totalProcessingTime = endTime - startTime;

      // Short delay to show the final results before closing
      setTimeout(async () => {
        setProcessingDialogOpen(false);

        if (!data.result || data.result.length === 0) {
          console.warn("No messages were extracted from the images");
          toast({
            title: "No messages found",
            description:
              "We couldn't detect any text messages in the images. Please try with clearer screenshots.",
            variant: "destructive",
          });
        } else {
          await processExtractedMessages(data.result);

          toast({
            title: "Processing complete",
            description: `Successfully extracted ${data.result.length} messages from your images.`,
          });
        }

        console.log(
          `Image processing completed in ${totalProcessingTime.toFixed(
            2
          )}ms (${(totalProcessingTime / 1000).toFixed(2)}s)`
        );
        scrollToBottom();
      }, 1000);
    } catch (error) {
      console.error("Error processing images:", error);
      Sentry.captureException(error);
      setProcessingDialogOpen(false);
      toast({
        title: "Error processing images",
        description:
          "There was a problem extracting text from your images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setImagesProcessing(false);
        e.target.value = "";
      }, 1000);
    }
  };

  const dismissQuickTip = () => {
    setShowQuickTip(false);
    window.localStorage.setItem(QUICK_TIP_DISMISSED_KEY, "true");
  };

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  const userMessageRef = useRef<HTMLDivElement>(null);

  // Function to decrement free requests remaining
  const decrementFreeRequests = async () => {
    if (
      userData?.uid &&
      userData.freeRequestsRemaining &&
      userData.freeRequestsRemaining > 0
    ) {
      try {
        const userRef = doc(db, `users/${userData.uid}`);
        await setDoc(
          userRef,
          {
            freeRequestsRemaining: userData.freeRequestsRemaining - 1,
          },
          { merge: true }
        );
      } catch (error) {
        console.error("Failed to update free requests:", error);
        Sentry.captureException(error);
      }
    }
  };

  const handleAnalyzeChat = async () => {
    if (!ensureAccess()) return;

    setTimeout(scrollToBottom, 100);
    try {
      await analyzeChat();
      // Decrement free requests if successful
      if (!access.hasAccess && canUseFreeRequest) {
        decrementFreeRequests();
      }
      // Scroll the user message into view
      setTimeout(() => {
        userMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (error) {
      console.error("Failed to analyze chat:", error);
      // Sentry.captureException(error);
      if (
        error instanceof Error &&
        error.message.includes("Daily usage limit exceeded.")
      ) {
        toast({
          variant: "destructive",
          title: "Daily usage limit exceeded.",
          description:
            "You've reached your daily request limit. Please try again tomorrow.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Something went wrong.",
          description:
            "There was a problem with your request. Please try again later.",
        });
      }
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!ensureAccess()) return;

    setTimeout(scrollToBottom, 100);
    try {
      await generateRecommendations();
      // Decrement free requests if successful
      if (!access.hasAccess && canUseFreeRequest) {
        decrementFreeRequests();
      }
      // Scroll the user message into view
      setTimeout(() => {
        userMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (error) {
      console.error("Failed to generate recommendations:", error);
      // Sentry.captureException(error);

      if (
        error instanceof Error &&
        error.message.includes("Daily usage limit exceeded.")
      ) {
        toast({
          variant: "destructive",
          title: "Daily usage limit exceeded.",
          description:
            "You've reached your daily request limit. Please try again tomorrow.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Something went wrong.",
          description:
            "There was a problem with your request. Please try again later.",
        });
      }
    }
  };

  const handleSendMessage = async () => {
    if (!ensureAccess()) return;

    setTimeout(scrollToBottom, 100);
    const currentText = inputText.trim();
    if (currentText) {
      try {
        setInputText("");
        await generateAIResponse(currentText);
        // Decrement free requests if successful
        if (!access.hasAccess && canUseFreeRequest) {
          decrementFreeRequests();
        }
        // Scroll the user message into view
        setTimeout(() => {
          userMessageRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } catch (error) {
        console.error("Failed to generate AI response:", error);
        // Sentry.captureException(error);
        if (
          error instanceof Error &&
          error.message.includes("Daily usage limit exceeded.")
        ) {
          toast({
            variant: "destructive",
            title: "Daily usage limit exceeded.",
            description:
              "You've reached your daily request limit. Please try again tomorrow.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Something went wrong.",
            description:
              "There was a problem with your request. Please try again later.",
          });
        }
      }
    }
  };

  const handleSendAIMessage = async (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (responseLoading) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSendMessage();
    } else if (e.key === "Escape") {
      setInputText("");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case "u":
          e.preventDefault();
          handleNewMessage("target"); // Opens new message input for target
          break;
        case "i":
          e.preventDefault();
          handleNewMessage("user"); // Opens new message input for user
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showNewMessage]); // Updated dependency to showNewMessage since we're using handleNewMessage

  const handleNewMessage = (sender: "user" | "target") => {
    setShowNewMessage(sender);
  };

  const handleSaveNewMessage = (newMessage: string) => {
    // Remove the summarization code since it's now in the hook
    if (newMessage.trim()) {
      addNewMessage(newMessage, showNewMessage!).catch((error) => {
        console.error("Failed to save message:", error);
        // Sentry.captureException(error);
        // Handle error (show toast, etc.)
      });
      setShowNewMessage(null);
      setTimeout(scrollToBottom, 0);
    }
  };

  const handleEditMessage = async (
    chunkId: string,
    messageIndex: number,
    newContent: string,
    isNewMessage?: boolean
  ) => {
    try {
      await editMessage(chunkId, messageIndex, newContent, isNewMessage);
    } catch (error) {
      console.error("Failed to edit message:", error);
      // Sentry.captureException(error);
      // Handle error (show toast, etc.)
    }
  };

  const handleDeleteMessage = async (chunkId: string, messageIndex: number) => {
    try {
      await deleteMessage(chunkId, messageIndex);
    } catch (error) {
      console.error("Failed to delete message:", error);
      // Sentry.captureException(error);
      // Handle error (show toast, etc.)
    }
  };

  const handleUploadClick = () => {
    document.getElementById("screenshot-upload")?.click();
  };

  useEffect(() => {
    const scrollContainer = scrollAreaRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]"
    );

    const handleScroll = () => {
      if (scrollContainer) {
        const isAtBottom =
          scrollContainer.scrollHeight - scrollContainer.scrollTop <=
          scrollContainer.clientHeight + 100; // 100px threshold
        setShowScrollButton(!isAtBottom);
      }
    };

    // Initial check
    handleScroll();

    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [chunks]); // Add chunks as dependency to re-run when content changes

  if (loadingFirstTime) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="animate-spin text-purple-500" />
      </div>
    );
  }

  // if (loading) return null;

  return (
    <>
      <div className=" flex flex-col  border-gray-200 overflow-hidden mx-auto h-full bg-gray-50">
        <div className="sticky top-0 flex  text-gray-800 p-2 sm:p-4  border-gray-200  justify-center items-center ">
          {/* {isMobile ? <SidebarTrigger /> : <div className="w-7"></div>} */}
          {/* <div className="w-7"></div> */}
          <ConversationDropdownMenu
            conversationName={conversationName}
            targetName={targetName}
            conversationId={conversationId}
          />

          {/* <h2 className="text-xl font-bold text-center">{targetName}</h2> */}
          {/* <div className="w-7"></div> */}
        </div>
        <ScrollArea
          ref={scrollAreaRef}
          className="overscroll-none  [--radix-scroll-area-viewport]:flex [--radix-scroll-area-viewport]:flex-col [--radix-scroll-area-viewport]:justify-end min-h-[400px] h-full"
        >
          <div className=" max-w-3xl mx-auto h-full flex flex-col ">
            {!loading ? (
              <div className="py-4 flex-1 px-4 pt-1">
                {chunks.map((chunk) => (
                  <Chunk
                    key={chunk.id}
                    chunk={chunk}
                    isLastChunk={chunk.id === lastChunk?.id}
                    userName={userName}
                    targetName={targetName}
                    isLoading={chunk.id === lastChunk?.id && responseLoading}
                    defaultOpen={
                      chunk.id === lastChunk?.id && lastChunk?.aiChat !== null
                    }
                    onEditMessage={handleEditMessage}
                    onDeleteMessage={handleDeleteMessage}
                    onMoveMessage={moveMessage}
                    onDeleteAIChat={deleteAIChat}
                    messageRef={
                      chunk.id === lastChunk?.id ? userMessageRef : undefined
                    }
                  />
                ))}
                <div className="sm:min-h-24 flex flex-col">
                  {/* New Message Input */}
                  {showNewMessage && (
                    <MessageBubbleInput
                      isUser={showNewMessage === "user"}
                      userName={userName}
                      targetName={targetName}
                      onClose={() => setShowNewMessage(null)}
                      onSave={handleSaveNewMessage}
                    />
                  )}
                  {!showNewMessage &&
                    (!isNewChat ? (
                      <div className="grid w-full grid-cols-3 items-center gap-4">
                        <Button
                          variant="outlineRound"
                          className="w-full min-w-0 gap-1"
                          onClick={() => handleNewMessage("target")}
                        >
                          <MessageCirclePlus className="w-4 h-4 flex-shrink-0" />
                          <span className="hidden lg:inline">
                            Add Message from
                          </span>
                          <span className="truncate">{targetLabel}</span>
                        </Button>
                        <div className="relative flex-1 min-w-0">
                          <Input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleUploadScreenshot}
                            className="hidden"
                            id="screenshot-upload"
                          />
                          <Button
                            variant="outlineRound"
                            className="w-full gap-1"
                            onClick={handleUploadClick}
                          >
                            <Images className="w-4 h-4" />
                            <span>Upload</span>
                          </Button>
                        </div>
                        <Button
                          variant="outlineRound"
                          className="w-full min-w-0 gap-1"
                          onClick={() => handleNewMessage("user")}
                        >
                          <MessageCirclePlus className="w-4 h-4 flex-shrink-0" />
                          <span className="hidden lg:inline">
                            Add Message from
                          </span>
                          <span className="truncate">You</span>
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {showQuickTip && (
                          <div className="relative rounded-2xl border border-gray-200 bg-gray-100/70 px-4 py-3 pr-11 text-sm leading-normal text-gray-600 shadow-sm">
                            <div className="flex items-start gap-2">
                              <span aria-hidden="true">💡</span>
                              <p>
                                <span className="font-semibold text-gray-800">
                                  Quick tip:
                                </span>{" "}
                                Upload your chat or describe what's happening
                                with the girl, then ask Options AI what you need
                                help with.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={dismissQuickTip}
                              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                              aria-label="Dismiss quick tip"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        <div className="relative">
                          <Input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleUploadScreenshot}
                            className="hidden"
                            id="screenshot-upload"
                          />
                          <Button
                            variant="outlineRound"
                            className="flex-1 gap-1 w-full"
                            onClick={handleUploadClick}
                          >
                            <Images className="w-4 h-4" />
                            <span className="">
                              Upload Screenshots of Your Chat
                            </span>
                            {/* <span className="lg:hidden">Upload</span> */}
                          </Button>
                        </div>
                        <p className="text-center w-full text-xs font-semibold text-gray-600">
                          or tell Options AI what&apos;s happening below
                        </p>
                        {SHOW_MANUAL_MESSAGE_BUTTONS_IN_NEW_CHAT && (
                          <div className="w-full flex flex-row items-center justify-between gap-4">
                            <Button
                              variant="outlineRound"
                              className="flex-1 gap-1 min-w-0"
                              onClick={() => handleNewMessage("target")}
                            >
                              <MessageCirclePlus className="w-4 h-4 flex-shrink-0" />
                              <span className="hidden lg:inline">
                                Add Message from
                              </span>
                              <span className="truncate">{targetLabel}</span>
                            </Button>

                            <Button
                              variant="outlineRound"
                              className="flex-1 gap-1 min-w-0"
                              onClick={() => handleNewMessage("user")}
                            >
                              <MessageCirclePlus className="w-4 h-4 flex-shrink-0" />
                              <span className="hidden lg:inline">
                                Add Message from
                              </span>
                              <span className="truncate">You</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="flex-1"></div>
            )}

            <div
              className={`sticky bottom-0 z-10 w-full border-t border-gray-200 bg-gray-50 px-3 pt-2 transition-transform duration-300 sm:rounded-t-2xl sm:border md:px-4 ${
                showNewMessage
                  ? "translate-y-full sm:translate-y-0"
                  : "translate-y-0"
              }`}
              style={{
                paddingBottom:
                  "calc(0.5rem + env(safe-area-inset-bottom, 0px))",
              }}
            >
              {/* Optional footer fade intentionally disabled for a clean edge. */}
              {/* <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 -top-2 h-2 bg-gradient-to-b from-transparent to-gray-50"
              /> */}
              {showScrollButton && (
                <div className="pointer-events-none absolute inset-x-0 -top-14 z-20 flex justify-end px-5 sm:justify-center">
                  <Button
                    variant="outlineRound"
                    className="pointer-events-auto h-10 w-10 rounded-full border border-gray-100 bg-white shadow-lg hover:bg-gray-100"
                    onClick={scrollToBottom}
                  >
                    <ArrowDown className="h-10 w-10 text-black" />
                    <span className="sr-only">Scroll to bottom</span>
                  </Button>
                </div>
              )}
              <div className="w-full">
                <div className="mb-2 flex gap-3 px-2 sm:px-0">
                  <Button
                    variant="custom"
                    onClick={handleAnalyzeChat}
                    disabled={responseLoading}
                    className="h-9 flex-1 rounded-full bg-purple-100 text-[13px] font-medium text-purple-600 shadow-none hover:bg-purple-200"
                  >
                    Analyze Chat
                  </Button>
                  <Button
                    variant="custom"
                    onClick={handleGenerateRecommendations}
                    disabled={responseLoading}
                    className="h-9 flex-1 rounded-full bg-purple-100 text-[13px] font-medium text-purple-600 shadow-none hover:bg-purple-200"
                  >
                    Get Replies
                  </Button>
                </div>
                <div className="relative rounded-2xl border border-purple-300 bg-white shadow-[0_4px_16px_rgba(126,34,206,0.12)] transition-[border-color,box-shadow] duration-200 focus-within:border-purple-500 focus-within:shadow-[0_6px_24px_rgba(126,34,206,0.22)]">
                  <AutoResizeTextarea
                    placeholder={"Ask Options AI anything..."}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    className="relative min-h-[88px] max-h-40 resize-none overflow-auto rounded-2xl border-0 bg-transparent px-5 py-3 pr-16 text-base shadow-none placeholder:text-gray-500 focus-visible:border-0 focus-visible:ring-0 sm:max-h-48"
                    style={{
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                    onKeyDown={handleSendAIMessage}
                  />
                  <Button
                    variant="custom"
                    onClick={handleSendMessage}
                    className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-purple-600 p-0 text-white shadow-md hover:bg-purple-500 focus:ring-0 focus:ring-offset-0 [&_svg]:!size-5"
                    disabled={responseLoading}
                  >
                    <Send className="text-white" />
                    <span className="sr-only">Send message</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
      <ImageProcessingDialog
        isOpen={processingDialogOpen}
        totalImages={totalImages}
        currentImageIndex={currentImageIndex}
        extractedMessages={extractedMessages}
      />
    </>
  );
}
